import { useState, useRef } from 'react';
import { Dialog, Button } from './aip';
import type { TranscriptionSegment } from '../services/manageNewApi';
import { getSegmentAudioUrl } from '../services/manageNewApi';

export interface TranscriptionSegmentsModalProps {
  /** モーダルが開いているか */
  open: boolean;
  /** 閉じる時のコールバック */
  onClose: () => void;
  /** 議事録ID */
  minutesId: number;
  /** セグメントリスト */
  segments: TranscriptionSegment[];
}

/**
 * 経過時間をフォーマット (HH:MM:SS)
 * @param elapsedMs 経過時間（ミリ秒）
 */
const formatElapsedTime = (elapsedMs: number): string => {
  const totalSeconds = Math.floor(elapsedMs / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
};

/**
 * テキストを指定文字数で切り詰め
 */
const truncateText = (text: string, maxLength: number): string => {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength) + '...';
};

/**
 * 録音一覧モーダル
 */
export const TranscriptionSegmentsModal: React.FC<TranscriptionSegmentsModalProps> = ({
  open,
  onClose,
  minutesId,
  segments,
}) => {
  // 現在再生中のセグメントID
  const [playingSegmentId, setPlayingSegmentId] = useState<number | null>(null);
  // audio要素への参照
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // 再生/停止ボタンクリック
  const handlePlayClick = (segment: TranscriptionSegment) => {
    if (!segment.has_audio) return;

    // 同じセグメントをクリックした場合は停止
    if (playingSegmentId === segment.id) {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
      setPlayingSegmentId(null);
      return;
    }

    // 別のセグメントを再生
    if (audioRef.current) {
      audioRef.current.pause();
    }

    const audioUrl = getSegmentAudioUrl(minutesId, segment.id);
    const audio = new Audio(audioUrl);
    audioRef.current = audio;

    audio.onended = () => {
      setPlayingSegmentId(null);
    };

    audio.onerror = () => {
      console.error('Audio playback error');
      setPlayingSegmentId(null);
    };

    audio.play().catch((err) => {
      console.error('Failed to play audio:', err);
      setPlayingSegmentId(null);
    });

    setPlayingSegmentId(segment.id);
  };

  // モーダルを閉じる時に再生を停止
  const handleClose = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    setPlayingSegmentId(null);
    onClose();
  };

  // 1件目の受信日時を基準時刻として取得
  const baseTime = segments.length > 0 ? new Date(segments[0].received_at).getTime() : 0;

  // 経過時間を計算
  const getElapsedTime = (receivedAt: string): string => {
    const elapsed = new Date(receivedAt).getTime() - baseTime;
    return formatElapsedTime(elapsed);
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      title="録音一覧"
      type="modal"
      className="transcription-segments-modal"
      footer={
        <Button buttonType="outline" size="md" onClick={handleClose}>
          閉じる
        </Button>
      }
    >
      <div className="transcription-segments-modal__content">
        {segments.length === 0 ? (
          <div className="transcription-segments-modal__empty">
            録音データがありません
          </div>
        ) : (
          <div className="transcription-segments-modal__table-wrapper">
            <table className="transcription-segments-modal__table">
              <thead>
                <tr>
                  <th className="transcription-segments-modal__th-num">#</th>
                  <th className="transcription-segments-modal__th-text">テキスト</th>
                  <th className="transcription-segments-modal__th-time">経過時間</th>
                  <th className="transcription-segments-modal__th-play">再生</th>
                </tr>
              </thead>
              <tbody>
                {segments.map((segment) => (
                  <tr key={segment.id} className="transcription-segments-modal__row">
                    <td className="transcription-segments-modal__td-num">
                      {segment.segment_sequence}
                    </td>
                    <td className="transcription-segments-modal__td-text" title={segment.text}>
                      {truncateText(segment.text, 40)}
                    </td>
                    <td className="transcription-segments-modal__td-time">
                      {getElapsedTime(segment.received_at)}
                    </td>
                    <td className="transcription-segments-modal__td-play">
                      <Button
                        buttonType="icon-only"
                        size="sm"
                        onClick={() => handlePlayClick(segment)}
                        disabled={!segment.has_audio}
                        aria-label={playingSegmentId === segment.id ? '停止' : '再生'}
                      >
                        <span className="material-symbols-outlined">
                          {playingSegmentId === segment.id ? 'stop' : 'play_arrow'}
                        </span>
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <style>{`
        .transcription-segments-modal {
          width: min(95%, 50rem) !important;
          max-height: 90vh;
        }

        .transcription-segments-modal__content {
          max-height: 60vh;
          overflow: hidden;
        }

        .transcription-segments-modal__empty {
          padding: 2rem;
          text-align: center;
          color: var(--text-tertiary);
        }

        .transcription-segments-modal__table-wrapper {
          max-height: 60vh;
          overflow-y: auto;
        }

        .transcription-segments-modal__table {
          width: 100%;
          border-collapse: collapse;
          font-size: 0.875rem;
        }

        .transcription-segments-modal__table thead {
          position: sticky;
          top: 0;
          background: var(--color-white);
          z-index: 1;
        }

        .transcription-segments-modal__table th {
          padding: 0.75rem 0.5rem;
          text-align: left;
          font-weight: 600;
          color: var(--color-gray600);
          border-bottom: 2px solid var(--color-gray200);
        }

        .transcription-segments-modal__th-num {
          width: 3rem;
          text-align: center !important;
        }

        .transcription-segments-modal__th-text {
          width: auto;
        }

        .transcription-segments-modal__th-time {
          width: 8rem;
        }

        .transcription-segments-modal__th-play {
          width: 4rem;
          text-align: center !important;
        }

        .transcription-segments-modal__row {
          transition: background-color 0.15s ease;
        }

        .transcription-segments-modal__row:hover {
          background-color: var(--color-gray50);
        }

        .transcription-segments-modal__table td {
          padding: 0.625rem 0.5rem;
          border-bottom: 1px solid var(--color-gray100);
          vertical-align: middle;
        }

        .transcription-segments-modal__td-num {
          text-align: center;
          color: var(--text-tertiary);
          font-family: monospace;
        }

        .transcription-segments-modal__td-text {
          color: var(--text-primary);
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          max-width: 300px;
        }

        .transcription-segments-modal__td-time {
          color: var(--text-secondary);
          font-family: monospace;
          font-size: 0.75rem;
        }

        .transcription-segments-modal__td-play {
          text-align: center;
        }

        .transcription-segments-modal__td-play button:disabled {
          opacity: 0.3;
          cursor: not-allowed;
        }

        /* スクロールバーのスタイル */
        .transcription-segments-modal__table-wrapper::-webkit-scrollbar {
          width: 6px;
        }

        .transcription-segments-modal__table-wrapper::-webkit-scrollbar-track {
          background: var(--color-gray100);
          border-radius: 3px;
        }

        .transcription-segments-modal__table-wrapper::-webkit-scrollbar-thumb {
          background: var(--color-gray300);
          border-radius: 3px;
        }

        .transcription-segments-modal__table-wrapper::-webkit-scrollbar-thumb:hover {
          background: var(--color-gray400);
        }
      `}</style>
    </Dialog>
  );
};

export default TranscriptionSegmentsModal;
